import { apiBase, pathOf, queryParams } from './offline-endpoints';

/**
 * What a teacher's write does to the responses kept on this device.
 *
 * Screens read their data locally first, so a save has two local effects:
 *
 *   1. Kept responses it may have changed are marked out of date, so the next
 *      read while the API is reachable fetches them fresh. Results, progress
 *      and student lists are computed by the server, and the device does not
 *      try to recompute them.
 *   2. An answer is written into the kept question screens straight away, so a
 *      teacher who reopens a question - online or not - sees what they saved.
 *
 * Pure functions over URLs and bodies, so the rules can be tested without
 * IndexedDB or HTTP.
 */

/** The ids a write touches. */
export interface WriteScope {
  attemptIds: Set<number>;
  assignmentIds: Set<number>;
  studentIds: Set<number>;
  assessmentIds: Set<number>;
  classIds: Set<number>;
}

const ID_FIELDS: ReadonlyArray<[keyof WriteScope, readonly string[]]> = [
  ['attemptIds', ['attempt_id', 'attemptId']],
  ['assignmentIds', ['assignment_id', 'assignmentId']],
  ['studentIds', ['user_id', 'student_id', 'studentId']],
  ['assessmentIds', ['assessment_id', 'assessmentId']],
  ['classIds', ['class_id', 'classId']],
];

/**
 * Collect the ids from a write's request body and, when it reached the server,
 * its response - a first save creates the attempt, and only the response
 * knows its id.
 */
export function writeScope(requestBody: unknown, responseBody: unknown = null): WriteScope {
  const scope: WriteScope = {
    attemptIds: new Set(),
    assignmentIds: new Set(),
    studentIds: new Set(),
    assessmentIds: new Set(),
    classIds: new Set(),
  };
  collectIds(requestBody, scope, 0);
  collectIds((responseBody as { data?: unknown } | null)?.data, scope, 0);
  return scope;
}

/**
 * Lists that roll up many students. Any answer can move their numbers, so they
 * are refreshed whenever the write touched their assessment - or, when the
 * write does not name one (a single answer does not), whenever it touched any.
 */
const AGGREGATE_PATHS: readonly RegExp[] = [
  /^\/api\/v1\/programs\/assessments\/students\/(\d+)$/,
  /^\/api\/v1\/programs\/assessments\/teacher\/intervals\/list\/(\d+)$/,
  /^\/api\/v1\/programs\/portfolios\/linear-history\/(\d+)$/,
  /^\/api\/v1\/programs\/assessments\/teacher-assessment\/bulk\/domains$/,
  /^\/api\/v1\/programs\/assessments\/teacher-assessment\/bulk\/items$/,
];

/** Whether a kept response may show something the write changed. */
export function isAffectedBy(
  urlWithParams: string,
  scope: WriteScope,
  base: string = apiBase(),
): boolean {
  const path = pathOf(urlWithParams, base);
  const params = queryParams(urlWithParams);
  const named = (set: Set<number>, names: readonly string[]) =>
    names.some((name) => {
      const value = params.get(name);
      return value !== null && set.has(Number(value));
    });

  // Anything that names a touched attempt, assignment, student or class.
  if (named(scope.attemptIds, ['attempt_id', 'attemptId'])) return true;
  if (named(scope.assignmentIds, ['assignment_id', 'assignmentId'])) return true;
  if (named(scope.studentIds, ['student_id', 'studentId'])) return true;
  if (named(scope.classIds, ['class_id', 'classId'])) return true;

  const attempt = path.match(/\/(?:results|bmc-results)\/(\d+)(?:\/|$)/);
  if (attempt && scope.attemptIds.has(Number(attempt[1]))) return true;

  const assignment =
    path.match(/\/(?:progress-tracker|sharing-state|portfolios\/history)\/(\d+)$/) ??
    path.match(/\/active-interval\/\d+\/(\d+)$/);
  if (assignment && scope.assignmentIds.has(Number(assignment[1]))) return true;

  for (const pattern of AGGREGATE_PATHS) {
    const match = path.match(pattern);
    if (!match) continue;
    const assessmentId = Number(match[1] ?? params.get('assessment_id'));
    return (
      scope.assessmentIds.size === 0 ||
      !Number.isInteger(assessmentId) ||
      assessmentId <= 0 ||
      scope.assessmentIds.has(assessmentId)
    );
  }
  return false;
}

// ── Answers written into kept question screens ─────────────────────────────

export interface LocalAnswer {
  attemptId: number;
  sectionItemId: number;
  response_json: unknown;
  response_text: unknown;
  response_numeric: unknown;
  remark_text: unknown;
}

const ANSWER_WRITES =
  /^\/api\/v1\/programs\/assessments\/teacher-assessment\/(save-response|save-bulk-responses)$/;

/**
 * The answers a write records, in a form the kept screens can take.
 *
 * Only for a known attempt. Without one there is nowhere safe to put the answer
 * locally: a student with no attempt yet has question screens that carry no
 * answers, and those screens are shared by every such student, so writing one
 * child's answer into them would show it on another child's screen.
 */
export function answersInWrite(
  urlWithParams: string,
  body: unknown,
  base: string = apiBase(),
): LocalAnswer[] {
  const match = pathOf(urlWithParams, base).match(ANSWER_WRITES);
  if (!match || !body || typeof body !== 'object') return [];

  const record = body as Record<string, unknown>;
  const attemptId = Number(record['attempt_id']);
  if (!Number.isInteger(attemptId) || attemptId <= 0) return [];

  const rows: unknown[] =
    match[1] === 'save-bulk-responses'
      ? Array.isArray(record['responses'])
        ? record['responses']
        : []
      : [record];

  return rows.flatMap((row) => {
    const answer = (row ?? {}) as Record<string, unknown>;
    const sectionItemId = Number(answer['section_item_id']);
    if (!Number.isInteger(sectionItemId) || sectionItemId <= 0) return [];
    return [
      {
        attemptId,
        sectionItemId,
        response_json: answer['response_json'] ?? null,
        response_text: answer['response_text'] ?? null,
        response_numeric: answer['response_numeric'] ?? null,
        remark_text: answer['remark_text'] ?? null,
      },
    ];
  });
}

const ITEMS_PATH = /^\/api\/v1\/teachers\/multiple-assessments\/section\/\d+\/items$/;
const OPTIONS_PATH = /^\/api\/v1\/teachers\/multiple-assessments\/section-item\/(\d+)\/options$/;

/**
 * A kept question-screen body with local answers written in, or null when the
 * response shows none of them.
 *
 * Mirrors what the API returns after the same save: getAssessmentQuestions
 * joins the answer onto each item row (`id` is the section item), and
 * getSectionItemOptions puts it on the question and derives each option's
 * `clicks` from the answer's selections.
 */
export function withLocalAnswers(
  urlWithParams: string,
  body: unknown,
  answers: readonly LocalAnswer[],
  base: string = apiBase(),
): unknown | null {
  if (!answers.length || !body || typeof body !== 'object') return null;

  const params = queryParams(urlWithParams);
  const attemptId = Number(params.get('attemptId') ?? params.get('attempt_id'));
  const relevant = answers.filter((answer) => answer.attemptId === attemptId);
  if (!relevant.length) return null;

  const path = pathOf(urlWithParams, base);
  const envelope = body as { data?: unknown };

  if (ITEMS_PATH.test(path) && Array.isArray(envelope.data)) {
    let changed = false;
    const data = envelope.data.map((row: unknown) => {
      const item = (row ?? {}) as Record<string, unknown>;
      const answer = relevant.find((a) => a.sectionItemId === Number(item['id']));
      if (!answer) return row;
      changed = true;
      return {
        ...item,
        response_json: normaliseResponseJson(answer.response_json),
        response_text: answer.response_text,
        response_numeric: answer.response_numeric,
        remark_text: answer.remark_text,
      };
    });
    return changed ? { ...envelope, data } : null;
  }

  const options = path.match(OPTIONS_PATH);
  if (options && envelope.data && typeof envelope.data === 'object') {
    const answer = relevant.find((a) => a.sectionItemId === Number(options[1]));
    if (!answer) return null;

    const question = envelope.data as Record<string, unknown>;
    const json = normaliseResponseJson(answer.response_json);
    const clicks = selectedClicks(json);
    return {
      ...envelope,
      data: {
        ...question,
        response_json: json,
        response_numeric: answer.response_numeric,
        remark_text: answer.remark_text,
        options: Array.isArray(question['options'])
          ? question['options'].map((raw: unknown) => {
              const option = (raw ?? {}) as Record<string, unknown>;
              const id = Number(option['id']);
              return { ...option, clicks: clicks.has(id) ? clicks.get(id) : null };
            })
          : question['options'],
      },
    };
  }

  return null;
}

// ── Internals ────────────────────────────────────────────────────────────────

function collectIds(value: unknown, scope: WriteScope, depth: number): void {
  if (depth > 3 || value === null || typeof value !== 'object') return;

  if (Array.isArray(value)) {
    for (const item of value) collectIds(item, scope, depth + 1);
    return;
  }

  const record = value as Record<string, unknown>;
  for (const [set, fields] of ID_FIELDS) {
    for (const field of fields) {
      const id = Number(record[field]);
      if (record[field] != null && Number.isInteger(id) && id > 0) scope[set].add(id);
    }
  }
  for (const nested of Object.values(record)) {
    if (nested && typeof nested === 'object') collectIds(nested, scope, depth + 1);
  }
}

/** The server stores option_id as a number and the screens compare it as one. */
function normaliseResponseJson(json: unknown): unknown {
  if (!json || typeof json !== 'object' || Array.isArray(json)) return json;
  const record = json as Record<string, unknown>;
  return record['option_id'] != null
    ? { ...record, option_id: Number(record['option_id']) }
    : json;
}

/** option id -> clicks, as the server derives selected-option rows from an answer. */
function selectedClicks(json: unknown): Map<number, unknown> {
  const clicks = new Map<number, unknown>();
  if (!json || typeof json !== 'object') return clicks;
  const record = json as Record<string, unknown>;

  if (Array.isArray(record['selected_options'])) {
    for (const selection of record['selected_options']) {
      if (selection !== null && typeof selection === 'object') {
        const s = selection as Record<string, unknown>;
        const id = Number(s['option_id']);
        if (Number.isInteger(id)) clicks.set(id, s['clicks'] ?? null);
      } else {
        const id = Number(selection);
        if (Number.isInteger(id)) clicks.set(id, null);
      }
    }
  } else if (record['option_id'] != null) {
    clicks.set(Number(record['option_id']), record['clicks'] ?? null);
  }
  return clicks;
}
